import React, {ReactNode, useCallback, useEffect, useRef, useState} from 'react'
import {FormattedMessage} from 'react-intl'
import {ICar} from '../../../models/Car'
import {IPost} from '../../../models/Post'
import {IStation} from '../../../models/Station'
import AngleLeft from '../../assets/icons/AngleLeft.svg'
import AngleRight from '../../assets/icons/AngleRight.svg'
import { usePrev } from '../hocs/hooks'
import CarItem from './cars/CarItem'
import CarTypeItem from './cars/CarTypeItem'
import PostItem from './cars/PostItem'
import ReviewItem, {IHomeReview} from './cars/ReviewItem'
import StationItem from './stations/StationItem'
import styles from './TopListItems.scss'

const DEFAULT_ANGLE_TOP = 133 //px
export const TopListItems = ({children, show = true, angleTop = DEFAULT_ANGLE_TOP}: {
  children?: ReactNode
  show?: boolean,
  angleTop?: number
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const scrollTo = useCallback((toLeft: boolean) => {
    const ele = ref.current
    if (!ele) return
    ele.scrollTo({
      left: toLeft ? ele.scrollLeft - 420 : ele.scrollLeft + 420,
      behavior: 'smooth'
    })
  }, [])
  const [isLeftTouch, setIsLeftTouch] = useState(true)
  const [isRightTouch, setIsRightTouch] = useState(false)
  const onScroll = useCallback(() => {
    const list = ref.current
    if (!list) return
    setIsLeftTouch(list.scrollLeft === 0)
    setIsRightTouch(list.scrollLeft + list.offsetWidth >= list.scrollWidth)
  }, [])
  useEffect(() => {
    ref.current?.addEventListener('scroll', onScroll)
    return () => ref.current?.removeEventListener('scroll', onScroll)
  }, [onScroll])
  return <div className={['flex-row d-flex justify-content-between position-relative'].join(' ')}>
    {show && <>
      <button
        onClick={() => scrollTo(true)}
        className={[
          'rounded-circle position-absolute border-0 bg-white shadow',
          isLeftTouch && 'd-none',
          styles.angleLeft,
        ].filter(Boolean).join(' ')}
        style={{ top: `${angleTop}px` }}
      >
        <AngleLeft className={styles.angle}/>
      </button>
      <div className={[styles.scroll].join(' ')} id="carList" ref={ref}>
        {children}
      </div>
      <button
        onClick={() => scrollTo(false)}
        className={[
          'rounded-circle position-absolute border-0 bg-white shadow',
          isRightTouch && 'd-none',
          styles.angleRight
        ].filter(Boolean).join(' ')}
        style={{ top: `${angleTop}px` }}
      >
        <AngleRight className={styles.angle}/>
      </button>
    </>}
  </div>
}

export const CarListItems = ({cars, multiTitle = false, isCatchphrase = false}: {
  cars: ReadonlyArray<ICar>
  multiTitle?: boolean
  isCatchphrase?: boolean
}) => <>
  {cars.length > 0
    ? <TopListItems show={!!cars.length}>
        <ul className="d-flex pl-0 mb-0 list-unstyled">
          {cars.map(car => <li key={car._id} className={[styles.item].join(' ')}>
            <CarItem car={car} multiTitle={multiTitle} isCatchphrase={isCatchphrase}/>
          </li>)}
          <li className={styles.dummy} />
        </ul>
      </TopListItems>
    : <div className="d-flex justify-content-center align-items-center py-3">
      <FormattedMessage id="popularCars.noVans" defaultMessage="No camper vans registered" />
    </div>
  }
</>

export const CarTypeListItems = ({carTypes}: {
  carTypes: Array<{_id:number, category:string, description:string, url:string, nameCategory?: string}>
  multiTitle?: boolean
  isCatchphrase?: boolean
}) => <>
  {carTypes.length > 0
    ? <TopListItems show={!!carTypes.length}>
        <ul className="d-flex pl-0 list-unstyled">
          {carTypes.map(carType => <li key={carType._id} className={[styles.item].join(' ')}>
            <CarTypeItem carType={carType} carTypePage={false}/>
          </li>)}
          <li className={styles.dummy} />
        </ul>
      </TopListItems>
    : <div className="d-flex justify-content-center align-items-center py-3">
      <FormattedMessage id="popularCars.noVans" defaultMessage="No camper vans registered" />
    </div>
  }
</>


export const StationListItems = ({stations}: {
  stations: ReadonlyArray<IStation>
}) => <>
{stations.length > 0
  ? <TopListItems show={!!stations.length}>
      <ul className="d-flex pl-0 list-unstyled">
        {stations.map(station => <li key={station._id} className={[styles.item].join(' ')}>
          <StationItem station={station}/>
        </li>)}
        <li className={styles.dummy} />
      </ul>
    </TopListItems>
  : <div className="d-flex justify-content-center align-items-center py-3">
    <FormattedMessage id="popularSpots.noSpots" defaultMessage="No camper spots registered" />
  </div> }
</>

export const ReviewListItems = ({reviews, angleState}: {
  reviews: ReadonlyArray<IHomeReview>
  angleState: { left: boolean, right: boolean }
}) => {
  const ref = useRef<HTMLUListElement>(null)
  const scrollTo = useCallback((toLeft: boolean) => {
    const ele = ref.current
    if (!ele) return
    ele.scrollTo({
      left: toLeft ? ele.scrollLeft - (ele.clientWidth + 2) : ele.scrollLeft + (ele.clientWidth + 2),
      behavior: 'smooth'
    })
  }, [])
  const prevAngleLeft = usePrev(angleState.left)
  const prevAngleRight = usePrev(angleState.right)

  useEffect(() => {
    if (angleState.left != prevAngleLeft) {
      scrollTo(true)
    } else if (angleState.right != prevAngleRight) {
      scrollTo(false)
    }
  }, [angleState.left, angleState.right])
  return <>
    {reviews.length > 0
      ? <TopListItems show={!!reviews.length} angleTop={57}>
        <ul className={['d-flex pl-0 list-unstyled', styles.reviewScroll].join(' ')} ref={ref}>
          {reviews.map(review => <li key={review._id} className={[styles.item].join(' ')}>
            <ReviewItem review={review}/>
          </li>)}
          <li className={styles.dummy} />
        </ul>
      </TopListItems>
      : <div className="d-flex justify-content-center align-items-center py-3">
        <FormattedMessage id="reviews.noReview" defaultMessage="No reviews registered" />
      </div> }
  </>
}

export const PostListItems = ({posts}: {
  posts: ReadonlyArray<IPost>
}) => <>
{posts.length > 0
  ? <TopListItems show={!!posts.length}>
      <ul className="d-flex pl-0 list-unstyled">
        {posts.map(post => <li key={post._id} className={[styles.item].join(' ')}>
          <PostItem post={post}/>
        </li>)}
        <li className={styles.dummy} />
      </ul>
    </TopListItems>
  : <div className="d-flex justify-content-center align-items-center py-3">
    <FormattedMessage id="posts.noPosts" defaultMessage="No articles registered" />
  </div> }
</>

TopListItems.displayName = 'TopListItems'
export default TopListItems
